import React, { useState, useMemo, useCallback, memo } from 'react';
import {
  Box, Card, Chip, Typography, Checkbox, Button,
  IconButton, Collapse, Tooltip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Stack, Divider, Alert, TextField,
  Dialog, DialogContent,
} from '@mui/material';
import {
  LocalShipping as ShipIcon,
  Print as PrintIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  CheckCircle as CheckCircleIcon,
  CalendarToday as CalendarIcon,
  PhotoLibrary as GalleryIcon,
  VisibilityOff as HideGalleryIcon,
  Email as EmailIcon,
  Replay as ReshipIcon,
  CreditCardOff as RefundIcon,
  Cancel as CancelIcon,
  Undo as RevertIcon,
  FlashOn as QuickShipIcon,
  Home as HomeIcon,
  ContentCopy as CopyIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  Edit as EditIcon,
  Check as SaveIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  PaletteOutlined as PaletteIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import CounterOrderCard from './CounterOrderCard';
import { S } from './cardStyles';
import ExcelLikeField from '../ExcelLikeField';
import ProductImageGallery from '../ProductImageGallery';
import HoverZoomImage from '../HoverZoomImage';
import ImageAnnotator from './ImageAnnotator';
import { UnifiedOrder, UnifiedOrderItem, OrderCardCallbacks, OrderSlotInfo, QueuePosition, PLATFORM_COLORS, PLATFORM_LABELS } from './types';
import { isIntlOrder, intlRegionOf, INTL_REGIONS } from '../../config/intlRegions';

// Bin lookup key for a single item — must match the key built in ReceivingQueuePage.
const itemSlotKey = (item: UnifiedOrderItem) =>
  `${(item.productName || '').trim()}|||${(item.variantOrSpec || '').trim()}`;

// ── Styles ────────────────────────────────────────────────────────────────────

// Card chrome lives in cardStyles.ts so CounterOrderCard renders identically.

// ── Props ─────────────────────────────────────────────────────────────────────

interface UnifiedOrderCardProps extends OrderCardCallbacks {
  order: UnifiedOrder;
  visibleItems: UnifiedOrderItem[];
  shippingInProgress?: boolean;
  onExpandAll?: (order: UnifiedOrder) => void;
  isExpanded?: boolean;
  slotInfo?: OrderSlotInfo;
  /** item.id → its place in the queue for that colour/size. 門市 cards only. */
  counterQueue?: Map<string, QueuePosition>;
}

// ── Component ─────────────────────────────────────────────────────────────────

const UnifiedOrderCard: React.FC<UnifiedOrderCardProps> = ({
  order, visibleItems, shippingInProgress,
  onToggleReceived, onUpdateField, onShip, onPrintLabel,
  onQuickShip, onHsinchuShip, onReship, onOutOfStockNotify,
  onRefund, onCancelOrder, onRevertShipped, onFulfill, onToggleOutOfStock,
  onStorefrontShipped, onTogglePin,
  onVariantUpdate, onSkuUpdate, onAddressUpdate, onCustomerUpdate,
  getProductLink, showLinks,
  onExpandAll, isExpanded: isExpandedProp,
  slotInfo, onResetSlot, onOpenColorPlate, onCounterReceive, onCounterCancelLine, onCounterCancelOrder, counterQueue,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [showGallery, setShowGallery] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressDraft, setAddressDraft] = useState('');
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [phoneDraft, setPhoneDraft] = useState('');
  const [zoomImages, setZoomImages] = useState<string[]>([]);
  const [zoomIndex, setZoomIndex] = useState(0);
  const [zoomItem, setZoomItem] = useState<UnifiedOrderItem | null>(null);
  const [annotating, setAnnotating] = useState(false);
  // 需注意 pin — order-level attention marker with optional note.
  const [editingPin, setEditingPin] = useState(false);
  const [pinNoteDraft, setPinNoteDraft] = useState('');

  const getItemImages = useCallback((item: UnifiedOrderItem): string[] => {
    const images: string[] = [];
    if (item.imageUrl) images.push(item.imageUrl);
    if (item.originalItem?.all_images) {
      try {
        const raw = item.originalItem.all_images;
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.filter((u: string) => typeof u === 'string' && u.length > 0);
        }
      } catch {}
    }
    // Shopify: product_images array
    if (item.originalItem?.product_images) {
      try {
        const raw = item.originalItem.product_images;
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.filter((u: string) => typeof u === 'string' && u.length > 0);
        }
      } catch {}
    }
    return images;
  }, []);

  const platformColor = PLATFORM_COLORS[order.platform];
  // A 門市 parcel is never 可以出貨 — it has no waybill to wait for and nothing to post, so
  // the old `allReceived && !hasTracking` would have parked it in 可以出貨 for ever. It is
  // routed to CounterOrderCard above anyway; this keeps the test honest regardless.
  const isReadyToShip = order.platform !== 'counter' && order.allReceived && !order.hasTrackingInfo;
  const hasTracking = order.hasTrackingInfo;
  const anyReceived = order.receivedCount > 0;
  const hasOutOfStock = order.items.some(i => i.outOfStock);

  const isPinned = !!order.pinned;
  const openPinEditor = useCallback(() => {
    setPinNoteDraft(order.pinNote || '');
    setEditingPin(true);
  }, [order.pinNote]);
  const commitPin = useCallback(() => {
    setEditingPin(false);
    onTogglePin?.(order, true, pinNoteDraft);
  }, [onTogglePin, order, pinNoteDraft]);
  const isCvs =order.deliveryMethod === 'pickup' && order.storeId;
  const isIntl = isIntlOrder(order);
  const intlRegion = intlRegionOf(order) || INTL_REGIONS.HK;
  // ⚠️ Not every intl order is 順豐. SG ships via Parxl, which has its own
  // booking lane (/parxl/create) and its own label endpoint — the backend
  // rejects /sf/* for it. SF-only actions (列印順豐單, 補寄) gate on isSfIntl;
  // every carrier-facing label reads intlRegion.carrierLabel.
  const isSfIntl = isIntl && intlRegion.carrier === 'sf';
  // 順豐台灣宅配 (storefront TW home delivery). 補寄 is offered only once a first
  // waybill exists, because /sf-domestic/reship mints METANOIA-TW-{order}-R{n} —
  // re-running the plain 順豐宅配 create would hand back the FIRST parcel's 單號.
  const isSfTwDomestic = order.platform === 'storefront' && !isIntl &&
    (order.deliveryMethod === 'home_delivery' || order.deliveryMethod === 'shipping');
  const hasSfTwWaybill = !!order.original?.sf_waybill_no;
  // Treat international (HK/MO/SG) as a home-delivery variant so the address block
  // renders + the "ship" action button path stays consistent (the parent page
  // routes SF-carried intl
  // through the SF International modal via handleShip).
  const isHomeDelivery = order.deliveryMethod === 'home_delivery' ||
    order.deliveryMethod === 'shipping' || isIntl;
  const canEditVariant = order.platform === 'shopify' || order.platform === 'storefront';
  // Shopee: 規格 edits are an OMS-only override (shopee_tracking.spec_override) — the buyer's
  // Shopee order keeps its original model, so the original is shown underneath.
  const canEditSpec = canEditVariant || order.platform === 'shopee';
  const hasOosColumn = order.platform === 'storefront' || order.platform === 'shopee';

  const cardAccent = hasTracking ? S.cardShipped : isReadyToShip ? S.cardReady : anyReceived ? S.cardPartial : {};

  const trackingDisplay = order.trackingNumber || (order.paymentNo
    ? (order.cvsValidationNo ? `${order.paymentNo}${order.cvsValidationNo}` : order.paymentNo) : '');

  const galleryOrder = useMemo(() => ({
    items: order.items.map(i => ({
      id: i.id, productName: i.productName, itemSpec: i.variantOrSpec,
      imageUrl: i.imageUrl, quantity: i.quantity, sku: i.sku, original: i.originalItem,
    })),
  }), [order.items]);

  const startAddressEdit = () => {
    setAddressDraft(order.shippingAddress || '');
    setEditingAddress(true);
  };
  const saveAddress = () => {
    onAddressUpdate?.(order, addressDraft);
    setEditingAddress(false);
  };

  // ── Action buttons ──────────────────────────────────────────────────────

  const renderActions = () => {
    const p = order.platform;
    return (
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {/* SHOPEE */}
        {p === 'shopee' && (
          <>
            <Button variant="contained" size="small" startIcon={<ShipIcon sx={{ fontSize: 16 }} />}
              disabled={shippingInProgress || !order.allReceived}
              onClick={(e) => { e.stopPropagation(); onShip(order); }}
              sx={{ ...S.btn, bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' }, '&.Mui-disabled': { bgcolor: '#e5e5e5' } }}>出貨</Button>
            <Tooltip title="列印出貨單"><IconButton size="small" onClick={(e) => { e.stopPropagation(); onPrintLabel(order); }}
              sx={{ border: '1px solid #e8e5e1', borderRadius: '10px', width: 36, height: 36, color: '#78716c' }}><PrintIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
          </>
        )}

        {/* SHOPIFY */}
        {p === 'shopify' && (
          <>
            <Tooltip title={!order.allReceived ? '需要先收齊所有商品' : hasTracking ? '標記已出貨 (Shopify)' : '需要先建立物流單'} arrow>
              <span><Button variant="outlined" size="small" startIcon={<ShipIcon sx={{ fontSize: 16 }} />}
                disabled={!order.allReceived} onClick={(e) => { e.stopPropagation(); onFulfill?.(order); }}
                sx={{ ...S.btn, borderColor: '#d4d4d8', color: '#3f3f46' }}>已出貨</Button></span>
            </Tooltip>
            {!hasTracking && isCvs && onQuickShip && (
              <Tooltip title={!order.allReceived ? `還有 ${order.items.filter(i => !i.received && !i.outOfStock).length} 件未收到` : '快速建立物流單'} arrow>
                <span><Button variant="contained" size="small" startIcon={<QuickShipIcon sx={{ fontSize: 16 }} />}
                  disabled={!order.allReceived} onClick={(e) => { e.stopPropagation(); onQuickShip(order); }}
                  sx={{ ...S.btn, bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}>快速出貨</Button></span>
              </Tooltip>
            )}
            {isHomeDelivery && onHsinchuShip && (
              <Tooltip title={!order.allReceived ? `還有 ${order.items.filter(i => !i.received && !i.outOfStock).length} 件未收到` : (isIntl ? `建立${intlRegion.carrierLabel}國際單 (${intlRegion.code})` : '建立宅配物流單')} arrow>
                <span><Button variant="contained" size="small" startIcon={<HomeIcon sx={{ fontSize: 16 }} />}
                  disabled={!order.allReceived}
                  onClick={(e) => { e.stopPropagation(); onHsinchuShip(order); }}
                  sx={{ ...S.btn, bgcolor: isIntl ? '#dc2626' : '#f59e0b', '&:hover': { bgcolor: isIntl ? '#b91c1c' : '#d97706' }, '&.Mui-disabled': { bgcolor: '#e5e5e5' } }}>
                  {isIntl ? `${intlRegion.nameZh}出貨` : '宅配出貨'}
                </Button></span>
              </Tooltip>
            )}
            {isCvs && !isHomeDelivery && (
              <Tooltip title={!order.allReceived ? `還有 ${order.items.filter(i => !i.received && !i.outOfStock).length} 件未收到` : '建立超商物流單'} arrow>
                <span><Button variant="contained" size="small" startIcon={<ShipIcon sx={{ fontSize: 16 }} />}
                  disabled={!order.allReceived}
                  onClick={(e) => { e.stopPropagation(); onShip(order); }}
                  sx={{ ...S.btn, bgcolor: '#4c5e8a', '&:hover': { bgcolor: '#3d4e75' }, '&.Mui-disabled': { bgcolor: '#e5e5e5' } }}>超商出貨</Button></span>
              </Tooltip>
            )}
            {hasTracking && <Tooltip title="列印出貨單"><IconButton size="small" onClick={(e) => { e.stopPropagation(); onPrintLabel(order); }}
              sx={{ border: '1px solid #e8e5e1', borderRadius: '10px', width: 36, height: 36, color: '#78716c' }}><PrintIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>}
          </>
        )}

        {/* STOREFRONT */}
        {p === 'storefront' && (
          <>
            {/* 已出貨 — mark as SHIPPED */}
            <Tooltip title={!order.allReceived ? `還有 ${order.items.filter(i => !i.received && !i.outOfStock).length} 件未收到` : '標記為已出貨'} arrow>
              <span><Button variant="outlined" size="small" startIcon={<ShipIcon sx={{ fontSize: 16 }} />}
                disabled={!order.allReceived}
                onClick={(e) => { e.stopPropagation(); onStorefrontShipped?.(order); }}
                sx={{ ...S.btn, borderColor: '#d4d4d8', color: '#3f3f46' }}>已出貨</Button></span>
            </Tooltip>
            {/* 快速出貨 — CVS without tracking */}
            {!hasTracking && isCvs && onQuickShip && (
              <Tooltip title={!order.allReceived ? `還有 ${order.items.filter(i => !i.received && !i.outOfStock).length} 件未收到` : '快速建立物流單'} arrow>
                <span><Button variant="contained" size="small" startIcon={<QuickShipIcon sx={{ fontSize: 16 }} />}
                  disabled={!order.allReceived} onClick={(e) => { e.stopPropagation(); onQuickShip(order); }}
                  sx={{ ...S.btn, bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}>快速出貨</Button></span>
              </Tooltip>
            )}
            {/* 超商出貨 / 宅配出貨 / 國際出貨 — MUST be disabled until all received (斷貨 items exempt) */}
            <Tooltip title={!order.allReceived ? `還有 ${order.items.filter(i => !i.received && !i.outOfStock).length} 件未收到，不可出貨` : (isIntl ? `建立${intlRegion.carrierLabel}國際單 (${intlRegion.code})` : isHomeDelivery ? '建立順豐宅配單 (台灣本島)' : '建立超商物流單')} arrow>
              <span>
                <Button variant="contained" size="small"
                  disabled={!order.allReceived}
                  startIcon={<ShipIcon sx={{ fontSize: 16 }} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isIntl) {
                      // Intl orders must use handleShip (routes to SF modal)
                      onShip(order);
                    } else if (isHomeDelivery) {
                      onHsinchuShip?.(order);
                    } else {
                      onShip(order);
                    }
                  }}
                  sx={{ ...S.btn, bgcolor: isIntl ? '#dc2626' : isHomeDelivery ? '#f59e0b' : '#4c5e8a', '&:hover': { bgcolor: isIntl ? '#b91c1c' : isHomeDelivery ? '#d97706' : '#3d4e75' }, '&.Mui-disabled': { bgcolor: '#e5e5e5' } }}>
                  {isIntl ? `${intlRegion.nameZh}出貨` : isHomeDelivery ? '順豐宅配' : '超商出貨'}
                </Button>
              </span>
            </Tooltip>
            {/* 列印 — intl orders get a prominent red print button (SF label URL
                for HK/MO, proxied Parxl PDF for SG), others the icon-only one. */}
            {hasTracking && isIntl && (
              <Tooltip title={`列印${intlRegion.carrierLabel}出貨單 (${intlRegion.code})`} arrow>
                <Button variant="contained" size="small" startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
                  onClick={(e) => { e.stopPropagation(); onPrintLabel(order); }}
                  sx={{ ...S.btn, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
                  列印{intlRegion.carrierLabel}單
                </Button>
              </Tooltip>
            )}
            {hasTracking && !isIntl && (
              <Tooltip title="列印出貨單">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onPrintLabel(order); }}
                  sx={{ border: '1px solid #e8e5e1', borderRadius: '10px', width: 36, height: 36, color: '#78716c' }}>
                  <PrintIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {/* 補寄: CVS pickup orders, SF intl orders that already have an SF
                waybill, OR TW 宅配 orders already shipped on 順豐. /sf/reship is
                SF-only — the backend rejects Parxl regions; TW goes to
                /sf-domestic/reship. */}
            {((isCvs && onReship) || (isSfIntl && hasTracking && onReship) ||
              (isSfTwDomestic && hasSfTwWaybill && onReship)) && order.status !== 'CANCELLED' && (
              <Button variant="outlined" size="small" startIcon={<ReshipIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => { e.stopPropagation(); onReship(order); }}
                sx={{ ...S.btn, borderColor: '#d4d4d8', color: '#3f3f46' }}>
                {isSfIntl || isSfTwDomestic ? '補寄 (順豐)' : '補寄'}
              </Button>
            )}
            {hasOutOfStock && order.status !== 'CANCELLED' && onOutOfStockNotify && (
              <Button variant="outlined" size="small" startIcon={<EmailIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => { e.stopPropagation(); onOutOfStockNotify(order); }}
                sx={{ ...S.btn, borderColor: '#fbbf24', color: '#92400e' }}>寄斷貨通知</Button>
            )}
            {onRefund && (order.paymentMethod === 'credit_card' || order.paymentMethod === 'line_pay') &&
              order.paymentStatus !== 'REFUNDED' && order.status !== 'CANCELLED' && (
              <Button variant="outlined" size="small" startIcon={<RefundIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => { e.stopPropagation(); onRefund(order); }}
                sx={{ ...S.btn, borderColor: '#fbbf24', color: '#92400e' }}>退刷</Button>
            )}
            {order.status === 'SHIPPED' && onRevertShipped && (
              <Button variant="outlined" size="small" startIcon={<RevertIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => { e.stopPropagation(); onRevertShipped(order); }}
                sx={{ ...S.btn, borderColor: '#d4d4d8', color: '#3f3f46' }}>退回未出貨</Button>
            )}
            {onCancelOrder && !['CANCELLED', 'SHIPPED', 'DELIVERED'].includes(order.status) && (
              <Button variant="outlined" size="small" startIcon={<CancelIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => { e.stopPropagation(); onCancelOrder(order); }}
                sx={{ ...S.btn, borderColor: '#fca5a5', color: '#dc2626' }}>取消</Button>
            )}
          </>
        )}
      </Stack>
    );
  };

  // 門市 gets a different card entirely — see CounterOrderCard for why. Everything below
  // this line assumes a customer, an address and a parcel that ships.
  if (order.platform === 'counter') {
    return <CounterOrderCard order={order} onCounterReceive={onCounterReceive} onUpdateField={onUpdateField} counterQueue={counterQueue}
      onCancelLine={onCounterCancelLine} onCancelOrder={onCounterCancelOrder}
      showLinks={showLinks} getProductLink={getProductLink} />;
  }

  return (
    <Card sx={{ ...S.card, ...cardAccent }}>
      {/* ═══ HEADER ═══ */}
      <Box sx={{ px: { xs: 2, md: 3.5 }, pt: 3, pb: 2 }}>
        {/* Row 1: Platform + Order ID + Count + Customer */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          <Chip label={PLATFORM_LABELS[order.platform]} size="small"
            sx={{ bgcolor: platformColor, color: '#fff', fontWeight: 700, fontSize: '0.8rem', height: 28, borderRadius: '8px' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#1a1a1a', fontFamily: '"DM Mono", monospace' }}>
            {order.orderNumber}
          </Typography>
          <Box sx={{
            px: 1.5, py: 0.5, borderRadius: '8px',
            bgcolor: order.allReceived ? '#dcfce7' : anyReceived ? '#fef3c7' : '#f5f3f0',
            fontWeight: 700, fontSize: '0.85rem',
            color: order.allReceived ? '#15803d' : anyReceived ? '#92400e' : '#78716c',
            fontFamily: '"DM Mono", monospace',
          }}>
            {order.receivedCount}/{order.totalCount}
          </Box>
          {order.allReceived && <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 22 }} />}
          {hasTracking && <Chip label="已建立物流單" icon={<ShipIcon />} size="small"
            sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 600, fontSize: '0.78rem', height: 28, border: '1px solid #bfdbfe' }} />}
          {/* 📌 需注意— order-level attention pin with optional note (shared across stations) */}
          {onTogglePin && !hasTracking && (
            editingPin ? (
              <TextField
                size="small"
                autoFocus
                value={pinNoteDraft}
                onChange={(e) => setPinNoteDraft(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onBlur={commitPin}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commitPin(); }
                  if (e.key === 'Escape') { setEditingPin(false); }
                }}
                placeholder="需注意的原因（可留空）"
                sx={{ width: 200, '& .MuiOutlinedInput-root': { fontSize: '0.8rem', height: 28 } }}
              />
            ) : isPinned ? (
              <Tooltip title={`需注意${order.pinnedBy ? ' · ' + order.pinnedBy : ''} — 點擊編輯備註，✕ 取消釘選`} arrow>
                <Chip
                  label={`📌 ${order.pinNote || '需注意'}`}
                  size="small"
                  onClick={(e) => { e.stopPropagation(); openPinEditor(); }}
                  onDelete={(e) => { e.stopPropagation(); onTogglePin?.(order, false); }}
                  sx={{
                    cursor: 'pointer', maxWidth: 240,
                    fontWeight: 700, fontSize: '0.78rem', height: 28,
                    bgcolor: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca',
                    '&:hover': { bgcolor: '#fecaca' },
                    '& .MuiChip-deleteIcon': { color: '#b91c1c', '&:hover': { color: '#7f1d1d' } },
                  }}
                />
              </Tooltip>
            ) : (
              <Tooltip title="標記為需注意（釘選）" arrow>
                <Chip
                  label="📌"
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onTogglePin?.(order, true, ''); openPinEditor(); }}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 700, fontSize: '0.85rem', height: 28,
                    bgcolor: '#f5f3f0', color: '#a8a29e', border: '1px solid #e8e5e1',
                    '&:hover': { bgcolor: '#fee2e2', color: '#b91c1c' },
                  }}
                />
              </Tooltip>
            )
          )}
          {/* 📦 貨架格 — which bin(s) this order's received items sit in. Shown even after
              出貨 (kept until the order leaves the page). Click to reset/reassign. */}
          {slotInfo && slotInfo.slots.length > 0 && (
            <Tooltip title={`此訂單的已收商品放在 ${slotInfo.slots.join('、')} 號格 — 點擊重新分配貨架格`} arrow>
              <Box
                onClick={(e) => { e.stopPropagation(); onResetSlot?.(order); }}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  px: 1.25, py: 0.4, borderRadius: '8px', cursor: 'pointer',
                  bgcolor: '#eef2ff', color: '#4338ca', fontWeight: 800, fontSize: '0.82rem',
                  border: '1px solid #c7d2fe', height: 28,
                  '&:hover': { bgcolor: '#e0e7ff' },
                }}>
                📦 {slotInfo.slots.join('、')} 號格
              </Box>
            </Tooltip>
          )}
          <Box sx={{ flex: 1 }} />
          {/* Customer name + phone — editable for Shopify/Storefront */}
          {editingCustomer && canEditVariant ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField size="small" value={nameDraft} onChange={(e) => setNameDraft(e.target.value)}
                placeholder="姓名" sx={{ width: 120, '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }} />
              <TextField size="small" value={phoneDraft} onChange={(e) => setPhoneDraft(e.target.value)}
                placeholder="電話" sx={{ width: 130, '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }} />
              <IconButton size="small" onClick={() => {
                onCustomerUpdate?.(order, nameDraft, phoneDraft);
                setEditingCustomer(false);
              }} sx={{ color: '#22c55e' }}><SaveIcon fontSize="small" /></IconButton>
              <IconButton size="small" onClick={() => setEditingCustomer(false)} sx={{ color: '#a8a29e' }}><CloseIcon fontSize="small" /></IconButton>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: canEditVariant ? 'pointer' : 'default' }}
              onClick={() => {
                if (!canEditVariant) return;
                setNameDraft(order.customerName || '');
                setPhoneDraft(order.customerPhone || '');
                setEditingCustomer(true);
              }}>
              <PersonIcon sx={{ fontSize: 18, color: '#a8a29e' }} />
              <Typography sx={{ fontSize: '0.95rem', color: '#3f3f46', fontWeight: 600 }}>{order.customerName}</Typography>
              {order.customerPhone && (
                <Typography sx={{ fontSize: '0.85rem', color: '#a8a29e' }}>{order.customerPhone}</Typography>
              )}
              {canEditVariant && <EditIcon sx={{ fontSize: 14, color: '#d4d4d8', ml: 0.5 }} />}
            </Box>
          )}
        </Box>

        {/* Row 2: Meta chips + Tracking */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center', mb: 2 }}>
          <Box sx={S.metaChip}><CalendarIcon sx={{ fontSize: 14 }} />{order.orderDateDisplay}</Box>
          {order.totalAmount && order.totalAmount > 0 && (
            <Box sx={{ ...S.metaChip, fontWeight: 700, color: '#57534e' }}>${Math.floor(order.totalAmount)}</Box>
          )}
          {/* Payment status — Shopify & Storefront only */}
          {(order.platform === 'shopify' || order.platform === 'storefront') && (() => {
            const pm = order.paymentMethod || order.original?.payment_method || '';
            const pmLabel = pm === 'credit_card' ? '信用卡'
              : pm === 'line_pay' ? 'LINE Pay'
              : pm === 'store_cod' ? '取貨付款'
              : pm === 'cvs' ? '超商付款'
              : pm || '';

            // 貨到付款 is collected at the counter, so it is NEVER prepaid — no
            // order status can make it so. Without this guard a 補寄 COD order
            // (written as ORDERED) rendered "已付款 (取貨付款)", telling the
            // warehouse not to collect money it still has to collect.
            const isCod = pm === 'store_cod' || pm === 'cod';

            let isPaid = false;
            if (order.platform === 'shopify') {
              isPaid = order.status === 'paid' || order.status === 'partially_refunded';
            } else if (!isCod) {
              isPaid = order.paymentStatus === 'SUCCESS' ||
                ['PAID', 'ORDERED'].includes((order.original?.status || '').toUpperCase()) ||
                pm === 'credit_card' || pm === 'line_pay';
            }

            return (
              <Box sx={{
                ...S.metaChip,
                bgcolor: isPaid ? '#dcfce7' : '#fef3c7',
                color: isPaid ? '#15803d' : '#92400e',
                fontWeight: 600,
              }}>
                {isPaid ? '已付款' : '待付款'}{pmLabel ? ` (${pmLabel})` : ''}
              </Box>
            );
          })()}
          {isCvs && order.storeName && (<Box sx={S.metaChip}><StoreIcon sx={{ fontSize: 14 }} />{order.storeName}</Box>)}
          {isIntl ? (
            <Box sx={{
              ...S.metaChip,
              bgcolor: '#fef2f2',
              color: '#b91c1c',
              fontWeight: 700,
              border: '1px solid #fecaca',
            }}>
              <HomeIcon sx={{ fontSize: 14 }} />國際配送 · {intlRegion.nameZh} ({intlRegion.carrierLabel})
            </Box>
          ) : isHomeDelivery ? (
            <Box sx={S.metaChip}>
              <HomeIcon sx={{ fontSize: 14 }} />
              {order.platform === 'storefront' ? '宅配 (順豐)' : '宅配'}
            </Box>
          ) : null}
          {/* Shopee: logistics type + order status */}
          {order.platform === 'shopee' && order.shippingCarrier && (
            <Box sx={S.metaChip}><ShipIcon sx={{ fontSize: 14 }} />{order.shippingCarrier}</Box>
          )}
          {(order.platform === 'shopee' || order.status === 'CANCELLED') && (() => {
            const s = (order.status || '').toUpperCase();
            const statusMap: Record<string, { label: string; bg: string; color: string }> = {
              READY_TO_SHIP: { label: '未出貨', bg: '#fef3c7', color: '#92400e' },
              PROCESSED: { label: '已按出貨', bg: '#dbeafe', color: '#1e40af' },
              IN_CANCEL: { label: '取消中', bg: '#fee2e2', color: '#991b1b' },
              CANCELLED: { label: '已取消', bg: '#fecaca', color: '#991b1b' },
            };
            const info = statusMap[s];
            return info ? (
              <Box sx={{ ...S.metaChip, bgcolor: info.bg, color: info.color, fontWeight: 600 }}>{info.label}</Box>
            ) : null;
          })()}
          {trackingDisplay && (
            <Tooltip title="點擊複製" arrow>
              <Box onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(trackingDisplay); }}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  px: 1.5, py: 0.4, borderRadius: '8px',
                  bgcolor: '#eff6ff', color: '#1d4ed8', fontSize: '0.82rem', fontWeight: 700,
                  fontFamily: '"DM Mono", monospace', cursor: 'pointer', border: '1px solid #bfdbfe',
                  '&:hover': { bgcolor: '#dbeafe' },
                }}>
                <CopyIcon sx={{ fontSize: 13 }} />{trackingDisplay}
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Address for 宅配 / 國際配送 orders */}
        {isHomeDelivery && order.shippingAddress && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, px: 2, py: 1.25,
            bgcolor: isIntl ? '#fef2f2' : '#fafaf8',
            borderRadius: '10px',
            border: isIntl ? '1px solid #fecaca' : '1px solid #f0eeea',
          }}>
            <HomeIcon sx={{ fontSize: 16, color: isIntl ? '#b91c1c' : '#a8a29e', flexShrink: 0 }} />
            {isIntl && (
              <Typography sx={{ fontSize: '0.72rem', color: '#b91c1c', fontWeight: 800, letterSpacing: 0.5, flexShrink: 0 }}>
                {intlRegion.code}
              </Typography>
            )}
            {editingAddress ? (
              <>
                <TextField size="small" fullWidth value={addressDraft} onChange={(e) => setAddressDraft(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }} />
                <IconButton size="small" onClick={saveAddress} sx={{ color: '#22c55e' }}><SaveIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => setEditingAddress(false)} sx={{ color: '#a8a29e' }}><CloseIcon fontSize="small" /></IconButton>
              </>
            ) : (
              <>
                <Typography sx={{ fontSize: '0.85rem', color: isIntl ? '#7f1d1d' : '#57534e', flex: 1, fontWeight: isIntl ? 600 : 400 }}>{order.shippingAddress}</Typography>
                {onAddressUpdate && (order.platform === 'shopify' || order.platform === 'storefront') && (
                  <IconButton size="small" onClick={startAddressEdit} sx={{ color: '#a8a29e' }}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                )}
              </>
            )}
          </Box>
        )}

        {/* Row 3: Actions + Gallery/Collapse */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
          {renderActions()}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
            <Button variant="text" size="small"
              startIcon={showGallery ? <HideGalleryIcon sx={{ fontSize: 16 }} /> : <GalleryIcon sx={{ fontSize: 16 }} />}
              onClick={() => setShowGallery(!showGallery)}
              sx={{ textTransform: 'none', color: '#a8a29e', fontSize: '0.8rem', '&:hover': { color: '#57534e' } }}>
              {showGallery ? '隱藏' : '相簿'}
            </Button>
            <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ color: '#a8a29e' }}>
              {expanded ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ borderColor: '#f0eeea' }} />

      {/* ═══ READY TO SHIP BANNER ═══ */}
      {order.allReceived && !hasTracking && (
        <Alert severity="success" icon={<ShipIcon />}
          sx={{ mx: 2.5, my: 1.5, border: '1px solid #bbf7d0', borderRadius: '10px', py: 0.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
            {order.items.some(i => i.outOfStock) ? '可出貨商品已收到 — 可以出貨' : '全部商品已收到 — 可以出貨'}
            {slotInfo && slotInfo.slots.length > 0 ? ` · 在 ${slotInfo.slots.join('、')} 號格` : ''}
          </Typography>
        </Alert>
      )}

      {/* ═══ 久候未到齊 — STALE BIN ALERT ═══ */}
      {slotInfo?.stale && !hasTracking && (
        <Alert severity="warning"
          sx={{ mx: 2.5, my: 1.5, border: '1px solid #fde68a', borderRadius: '10px', py: 0.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#92400e' }}>
            ⚠ 此訂單已在貨架等待超過 {slotInfo.staleDays} 天{slotInfo.slots.length > 0 ? `（${slotInfo.slots.join('、')} 號格）` : ''} — 請確認是否缺件或催貨
          </Typography>
        </Alert>
      )}

      {/* ═══ RESHIP (補寄) HISTORY ═══ */}
      {order.reships && order.reships.length > 0 && (
        <Alert
          severity="info"
          icon={<ReshipIcon />}
          sx={{ mx: 2.5, my: 1.5, border: '1px solid #bae6fd', borderRadius: '10px', py: 0.5 }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
              補寄紀錄（{order.reships.length} 次）
            </Typography>
            {order.reships.map((rs, idx) => {
              const dt = rs.createdAt ? new Date(rs.createdAt) : null;
              const when = dt
                ? `${dt.getFullYear()}/${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`
                : '—';
              const isSfReship = rs.carrier === 'sf' || rs.carrier === 'sf_tw';
              const carrierTag = rs.carrier === 'sf_tw' ? '順豐宅配 ' : rs.carrier === 'sf' ? '順豐 ' : '';
              return (
                <Box key={rs.reshipOrderId} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: '0.78rem', color: '#334155', fontFamily: 'monospace' }}>
                    #{idx + 1} · {when} · {carrierTag}追蹤碼 {rs.trackingNumber || '—'}
                  </Typography>
                  {isSfReship && rs.labelUrl && (
                    <Typography
                      component="a"
                      href={rs.labelUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      列印順豐單
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Alert>
      )}

      {/* ═══ EXCHANGE RETURN STATUS BANNER ═══ */}
      {order.exchangeRequest && (
        <Alert
          severity={order.exchangeRequest.customerShippedReturn ? 'info' : 'error'}
          sx={{ mx: 2.5, my: 1.5, border: '1px solid', borderColor: order.exchangeRequest.customerShippedReturn ? '#bae6fd' : '#fecaca', borderRadius: '10px', py: 0.5 }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
              {order.exchangeRequest.customerShippedReturn
                ? '✅ 客戶已寄回舊商品'
                : '🚫 客人還未寄回舊商品 — 請不要出貨！'}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#78716c' }}>
              換貨申請 {order.exchangeRequest.requestId}
              {order.exchangeRequest.originalOrderNumber ? ` · 原訂單 #${order.exchangeRequest.originalOrderNumber}` : ''}
              {order.exchangeRequest.returnTrackingNumber ? ` · 退貨追蹤碼 ${order.exchangeRequest.returnTrackingNumber}` : ''}
            </Typography>
          </Box>
        </Alert>
      )}

      {/* ═══ GALLERY ═══ */}
      {showGallery && (
        <Box sx={{ p: 2.5, bgcolor: '#fafaf8', borderBottom: '1px solid #f0eeea' }}>
          <ProductImageGallery order={galleryOrder} />
        </Box>
      )}

      {/* ═══ PARTIAL VIEW WARNING ═══ */}
      {visibleItems.length < order.items.length && !order.allReceived && (
        <Alert
          severity="warning"
          sx={{
            mx: 2.5, my: 1, borderRadius: '10px', py: 0.5,
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
              此訂單共 {order.totalCount} 件商品，已收 {order.receivedCount}/{order.totalCount}{order.items.some(i => i.outOfStock) ? `，斷貨 ${order.items.filter(i => i.outOfStock).length} 件` : ''} — 還有 {order.items.filter(i => !i.received && !i.outOfStock).length} 件未收到
            </Typography>
            <Typography
              onClick={() => onExpandAll?.(order)}
              sx={{ fontSize: '0.8rem', color: '#4c5e8a', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              {isExpandedProp ? '僅顯示搜尋結果' : '顯示全部商品'}
            </Typography>
          </Box>
        </Alert>
      )}

      {/* ═══ ITEMS TABLE ═══ */}
      <Collapse in={expanded}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="medium">
            <TableHead>
              <TableRow sx={S.tableHeader}>
                <TableCell width="85px" align="center">圖片</TableCell>
                <TableCell>產品名稱</TableCell>
                <TableCell width="13%">規格</TableCell>
                <TableCell width="50px" align="center">數量</TableCell>
                <TableCell width="11%">標籤</TableCell>
                <TableCell width="13%">備註</TableCell>
                <TableCell width="55px" align="center">收貨</TableCell>
                {hasOosColumn && <TableCell width="50px" align="center">缺貨</TableCell>}
                <TableCell width="9%">SKU</TableCell>
                {showLinks && <TableCell width="55px" align="center">連結</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleItems.map((item) => (
                <TableRow key={item.id} sx={S.tableRow(item.received, item.outOfStock)}>
                  {/* Image — click to open gallery lightbox; show referenceImage if set */}
                  <TableCell align="center">
                    {(item.referenceImage || item.imageUrl) ? (
                      <Box sx={{ position: 'relative', display: 'inline-block' }}>
                        <HoverZoomImage
                          src={(item.referenceImage || item.imageUrl) as string}
                          width={80}
                          height={80}
                          borderColor={item.referenceImage ? '#f59e0b' : '#e8e5e1'}
                          onClick={() => {
                            const imgs = getItemImages(item);
                            // Prepend reference image if set and not already in the list
                            if (item.referenceImage && !imgs.includes(item.referenceImage)) {
                              imgs.unshift(item.referenceImage);
                            }
                            setZoomImages(imgs); setZoomIndex(0); setZoomItem(item);
                          }}
                          thumbSx={{
                            borderRadius: '10px',
                            border: item.referenceImage ? '2px solid #f59e0b' : '1px solid #e8e5e1',
                          }}
                        />
                        {(() => { const cnt = getItemImages(item).length; return cnt > 1 ? (
                          <Box sx={{
                            position: 'absolute', bottom: 2, right: 2, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
                            fontSize: '0.65rem', fontWeight: 700, px: 0.6, py: 0.15, borderRadius: '4px',
                            pointerEvents: 'none',
                          }}>{cnt}</Box>
                        ) : null; })()}
                        {item.referenceImage && (
                          <Box sx={{
                            position: 'absolute', top: 2, left: 2, bgcolor: '#f59e0b', color: '#fff',
                            fontSize: '0.55rem', fontWeight: 700, px: 0.5, py: 0.1, borderRadius: '3px',
                            pointerEvents: 'none', lineHeight: 1.2,
                          }}>參考</Box>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ width: 80, height: 80, bgcolor: '#f5f3f0', borderRadius: '10px' }} />
                    )}
                  </TableCell>

                  {/* Product Name */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '0.9rem', color: '#1a1a1a' }}>{item.productName}</Typography>
                      {item.received && <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 16, flexShrink: 0 }} />}
                    </Box>
                  </TableCell>

                  {/* Variant / Spec — editable for Shopify/Storefront, override-only for Shopee.
                      🎨 opens the 選色 plate when the colour name is ambiguous. */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {canEditSpec ? (
                          <ExcelLikeField
                            value={item.specOverride || item.variantOrSpec || '—'}
                            onSave={(v) => onVariantUpdate?.(order, item, v)}
                            placeholder="規格"
                          />
                        ) : (
                          <Typography sx={{ fontSize: '0.85rem', color: '#57534e' }}>{item.variantOrSpec || '—'}</Typography>
                        )}
                        {item.specOverride && (
                          <Tooltip title="已在 OMS 改規格 — 蝦皮訂單與物流單仍是原規格" arrow>
                            <Typography sx={{ fontSize: '0.72rem', color: '#a8a29e', textDecoration: 'line-through', mt: 0.25 }}>
                              原：{item.variantOrSpec || '—'}
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                      {onOpenColorPlate && (
                        <Tooltip title="選色對照 — 官網全色平拍圖" arrow>
                          <IconButton size="small" onClick={() => onOpenColorPlate(item)}
                            sx={{ p: 0.25, flexShrink: 0, color: '#a8a29e', '&:hover': { color: '#2B3A4E', bgcolor: 'transparent' } }}>
                            <PaletteIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>

                  {/* Quantity */}
                  <TableCell align="center">
                    <Box sx={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      minWidth: 32, height: 32, borderRadius: '8px',
                      bgcolor: item.quantity > 1 ? '#fef3c7' : '#f5f3f0',
                      border: item.quantity > 1 ? '1px solid #fde68a' : 'none',
                      fontWeight: 700, fontSize: '0.9rem',
                      color: item.quantity > 1 ? '#92400e' : '#57534e',
                      fontFamily: '"DM Mono", monospace',
                    }}>{item.quantity}</Box>
                  </TableCell>

                  {/* Tag */}
                  <TableCell>
                    <ExcelLikeField value={item.tag || ''} onSave={(v) => onUpdateField(order, item, 'tag', v)} placeholder="標籤" />
                  </TableCell>

                  {/* Note */}
                  <TableCell>
                    <ExcelLikeField value={item.note || ''} onSave={(v) => onUpdateField(order, item, 'note', v)} placeholder="備註" multiline rows={1} />
                  </TableCell>

                  {/* Received */}
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                      <Tooltip title={item.received && item.receivedBy
                        ? `${item.receivedBy} · ${item.receivedAt ? new Date(item.receivedAt).toLocaleDateString('zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}`
                        : '標記為已收'} arrow>
                        <Checkbox checked={item.received}
                          onChange={(e) => onToggleReceived(order, item, e.target.checked)}
                          color="success"
                          sx={{ p: 0.5, '&:hover': { transform: 'scale(1.08)' }, '& .MuiSvgIcon-root': { fontSize: 26 }, '&.Mui-checked': { color: '#22c55e' } }} />
                      </Tooltip>
                      {(() => {
                        const bin = slotInfo?.items.get(itemSlotKey(item))
                          ?? (!item.variantOrSpec ? slotInfo?.items.get(`${(item.productName || '').trim()}|||Default`) : undefined);
                        return bin != null ? (
                          <Box sx={{
                            px: 0.75, borderRadius: '6px', bgcolor: '#eef2ff', color: '#4338ca',
                            fontWeight: 800, fontSize: '0.72rem', lineHeight: 1.6, whiteSpace: 'nowrap',
                            border: '1px solid #c7d2fe',
                          }}>放 {bin} 號</Box>
                        ) : null;
                      })()}
                    </Box>
                  </TableCell>

                  {/* Out of stock — Storefront + Shopee */}
                  {hasOosColumn && (
                    <TableCell align="center">
                      <Checkbox checked={item.outOfStock || false}
                        onChange={(e) => onToggleOutOfStock?.(order, item, e.target.checked)}
                        sx={{ p: 0.5, color: '#e65100', '&.Mui-checked': { color: '#e65100' }, '& .MuiSvgIcon-root': { fontSize: 22 } }} />
                    </TableCell>
                  )}

                  {/* SKU — editable for Shopify/Storefront */}
                  <TableCell>
                    {canEditVariant ? (
                      <ExcelLikeField
                        value={item.sku || ''}
                        onSave={(v) => onSkuUpdate?.(order, item, v)}
                        placeholder="SKU"
                      />
                    ) : (
                      <Typography sx={{ fontSize: '0.82rem', fontFamily: '"DM Mono", monospace', color: '#78716c' }}>{item.sku || '—'}</Typography>
                    )}
                  </TableCell>

                  {/* Link — admin only */}
                  {showLinks && (
                    <TableCell align="center">
                      {(() => {
                        const linkInfo = getProductLink?.(item.sku);
                        if (!linkInfo?.link && !linkInfo?.allLinks?.length) return null;
                        const links = linkInfo.allLinks || [linkInfo.link!];
                        return (
                          <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center' }}>
                            {links.map((url, idx) => (
                              <IconButton key={idx} size="small"
                                onClick={() => window.open(url, '_blank', 'noopener')}
                                sx={{ color: idx === 0 ? '#3b82f6' : '#8b7355', width: 28, height: 28 }}>
                                <OpenInNewIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            ))}
                          </Box>
                        );
                      })()}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {visibleItems.length < order.items.length && (
          <Box
            onClick={() => onExpandAll?.(order)}
            sx={{
              px: 3, py: 1.2, bgcolor: '#fafaf8', borderTop: '1px solid #f0eeea',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              '&:hover': { bgcolor: '#f0eeea' },
            }}
          >
            <Typography sx={{ fontSize: '0.8rem', color: '#78716c' }}>
              顯示 {visibleItems.length}/{order.totalCount} 項 · 已收 {order.receivedCount}/{order.totalCount}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#4c5e8a', fontWeight: 600 }}>
              {isExpandedProp ? '僅顯示搜尋結果' : '顯示全部商品'}
            </Typography>
          </Box>
        )}
      </Collapse>

      {/* Image gallery lightbox */}
      <Dialog open={zoomImages.length > 0} onClose={() => { setZoomImages([]); setZoomItem(null); setAnnotating(false); }} maxWidth="lg"
        PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.92)', boxShadow: 'none', borderRadius: '16px', overflow: 'hidden', maxHeight: '95vh' } }}>
        <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          {zoomImages.length > 0 && (
            <>
              {annotating && zoomItem ? (
                /* ─── Annotation mode ─── */
                <>
                  <Typography sx={{ color: '#fff', fontSize: '0.9rem', fontWeight: 600, mb: 0.5 }}>
                    標記「{zoomItem.variantOrSpec || zoomItem.productName}」的參考圖
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', mb: 1 }}>
                    在圖片上畫圈標記正確的顏色位置
                  </Typography>
                  <ImageAnnotator
                    imageUrl={zoomImages[zoomIndex]}
                    proxyUrl={`${api.defaults.baseURL}/api/products/proxy-image?url=${encodeURIComponent(zoomImages[zoomIndex])}`}
                    onSave={async (dataUrl) => {
                      if (!zoomItem) return;
                      try {
                        // Build per-color product key
                        const color = (zoomItem.variantOrSpec || '').trim().split(/\s*[-\/,]\s*/)[0] || '';
                        const sku = zoomItem.sku || '';
                        const productKey = sku ? (color ? `${sku}|${color}` : sku)
                          : `${zoomItem.productName}|${color || 'default'}`.toLowerCase().trim();
                        await api.post('/api/products/reference-image/base64', {
                          productKey,
                          referenceImage: dataUrl,
                        });
                        onUpdateField(order, zoomItem, 'referenceImage', dataUrl);
                      } catch (err) {
                        console.error('Failed to save annotated reference image:', err);
                      }
                      setAnnotating(false);
                      setZoomImages([]);
                      setZoomItem(null);
                    }}
                    onCancel={() => setAnnotating(false)}
                  />
                </>
              ) : (
                /* ─── Normal gallery view ─── */
                <>
                  {/* Item info bar */}
                  {zoomItem && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', maxWidth: '78vw', flexWrap: 'wrap' }}>
                      <Typography sx={{ color: '#fff', fontSize: '0.9rem', fontWeight: 600, flex: 1 }}>
                        {zoomItem.variantOrSpec || zoomItem.productName}
                      </Typography>
                      <Button size="small" variant="contained"
                        onClick={async (e) => {
                          e.stopPropagation();
                          const imgUrl = zoomImages[zoomIndex];
                          if (!zoomItem) return;
                          try {
                            const color = (zoomItem.variantOrSpec || '').trim().split(/\s*[-\/,]\s*/)[0] || '';
                            const sku = zoomItem.sku || '';
                            const productKey = sku ? (color ? `${sku}|${color}` : sku)
                              : `${zoomItem.productName}|${color || 'default'}`.toLowerCase().trim();
                            await api.post('/api/products/metadata', {
                              productKey,
                              referenceImage: imgUrl,
                            });
                            onUpdateField(order, zoomItem, 'referenceImage', imgUrl);
                            setZoomImages([]);
                            setZoomItem(null);
                          } catch { /* silently fail */ }
                        }}
                        sx={{
                          bgcolor: '#f59e0b', color: '#fff', textTransform: 'none', fontWeight: 700,
                          fontSize: '0.78rem', borderRadius: '8px', whiteSpace: 'nowrap',
                          '&:hover': { bgcolor: '#d97706' },
                        }}>
                        設為參考圖
                      </Button>
                      <Button size="small" variant="outlined"
                        onClick={(e) => { e.stopPropagation(); setAnnotating(true); }}
                        sx={{
                          color: '#f59e0b', borderColor: '#f59e0b', textTransform: 'none', fontWeight: 700,
                          fontSize: '0.78rem', borderRadius: '8px', whiteSpace: 'nowrap',
                          '&:hover': { borderColor: '#d97706', bgcolor: 'rgba(245,158,11,0.1)' },
                        }}>
                        畫圈標記
                      </Button>
                    </Box>
                  )}

                  {/* Main image */}
                  <Box component="img" src={zoomImages[zoomIndex]} alt=""
                    sx={{ maxWidth: '78vw', maxHeight: zoomItem ? '58vh' : '65vh', objectFit: 'contain', borderRadius: '10px' }} />

                  {/* Counter */}
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, fontSize: '0.8rem', fontFamily: '"DM Mono", monospace' }}>
                    {zoomIndex + 1} / {zoomImages.length}
                  </Typography>

                  {/* Thumbnail strip */}
                  {zoomImages.length > 1 && (
                    <Box sx={{
                      display: 'flex', gap: 1, overflowX: 'auto', maxWidth: '78vw',
                      py: 1, px: 0.5,
                      '&::-webkit-scrollbar': { height: 4 },
                      '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.3)', borderRadius: 2 },
                    }}>
                      {zoomImages.map((img, idx) => (
                        <Box key={idx}
                          component="img" src={img} alt=""
                          onClick={(e) => { e.stopPropagation(); setZoomIndex(idx); }}
                          sx={{
                            width: 64, height: 64, objectFit: 'cover', borderRadius: '8px', cursor: 'pointer',
                            flexShrink: 0, transition: 'all 0.15s ease',
                            border: idx === zoomIndex ? '2px solid #3b82f6' : '2px solid transparent',
                            opacity: idx === zoomIndex ? 1 : 0.5,
                            '&:hover': { opacity: 1 },
                          }} />
                      ))}
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default memo(UnifiedOrderCard);
